import { Link } from 'react-router-dom';
import { CalendarHeart, HandHeart, Handshake, Heart, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import PageShell from '../components/PageShell';
import SEO from '../components/SEO';
import { communityImages, PHOTO_MURAL, PHOTO_LEADERSHIP } from '../mock';
import { useI18n } from '../contexts/I18nContext';

const iconMap = { CalendarHeart, HandHeart, Handshake, Heart };

export default function GetInvolvedPage() {
  const { t } = useI18n();

  const ways = [
    { icon: 'CalendarHeart', title: t('gi.way1.title'), desc: t('gi.way1.desc'), cta: t('gi.way1.cta'), href: '/summer-series', color: 'coral', external: false },
    { icon: 'HandHeart', title: t('gi.way2.title'), desc: t('gi.way2.desc'), cta: t('gi.way2.cta'), href: '/get-involved#volunteer', color: 'teal', external: false },
    { icon: 'Handshake', title: t('gi.way3.title'), desc: t('gi.way3.desc'), cta: t('gi.way3.cta'), href: '/get-involved#partner', color: 'yellow', external: false },
    { icon: 'Heart', title: t('gi.way4.title'), desc: t('gi.way4.desc'), cta: t('gi.way4.cta'), href: '/donate', color: 'coral', external: false },
  ];

  const colorMap = {
    coral: { bg: 'bg-[#E5614C]', soft: 'bg-[#E5614C]/10' },
    teal: { bg: 'bg-[#5DCAB5]', soft: 'bg-[#5DCAB5]/15' },
    yellow: { bg: 'bg-[#FCD968]', soft: 'bg-[#FCD968]/35' },
  };

  return (
    <PageShell
      eyebrow={t('gi.eyebrow')}
      title={<>{t('gi.title1')} <span className="italic text-[#E5614C]">{t('gi.title2')}</span> {t('gi.title3')}</>}
      subtitle={t('gi.subtitle')}
      accent="coral"
    >
      <SEO
        title="Get Involved — Volunteer, Partner, or Support"
        description="There are many ways to join the GEMS @ The Arts community in Framingham, MA. Volunteer, become a partner, sponsor an event, or support local youth and artists through donation."
        path="/get-involved"
      />
      {/* Quick intro grid of community images */}
      <section className="pb-10">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {communityImages.slice(0, 4).map((src, i) => (
              <div key={i} className="aspect-[4/5] rounded-2xl overflow-hidden shadow-[0_18px_40px_-20px_rgba(42,27,26,0.3)] hover:-translate-y-1 transition-transform duration-300">
                <img src={src} alt={`GEMS @ The Arts community member ${i + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 Ways Cards */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <h2 className="font-serif-display text-[32px] sm:text-[40px] lg:text-[48px] font-bold text-[#2A1B1A] text-center">
            {t('gi.fourWays1')} <span className="italic text-[#E5614C]">{t('gi.fourWays2')}</span>
          </h2>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {ways.map((w) => {
              const Icon = iconMap[w.icon] || Heart;
              const cl = colorMap[w.color];
              return (
                <div
                  key={w.title}
                  className="group relative bg-white rounded-[24px] p-8 lg:p-10 border border-[#2A1B1A]/8 hover:border-transparent hover:shadow-[0_30px_60px_-25px_rgba(42,27,26,0.3)] hover:-translate-y-1.5 transition-[transform,box-shadow,border-color] duration-300 overflow-hidden"
                >
                  <div className={`absolute -top-12 -right-12 w-40 h-40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${cl.soft}`} />
                  <div className={`relative h-14 w-14 rounded-2xl ${cl.bg} ${w.color === 'yellow' ? 'text-[#2A1B1A]' : 'text-white'} flex items-center justify-center shadow-[0_10px_24px_-8px_rgba(0,0,0,0.25)]`}>
                    <Icon className="h-7 w-7" />
                  </div>
                  <h3 className="relative font-serif-display text-[26px] lg:text-[30px] font-bold text-[#2A1B1A] mt-6">{w.title}</h3>
                  <p className="relative mt-3 text-[#2A1B1A]/70 text-[15px] leading-relaxed">{w.desc}</p>
                  <div className="relative mt-7">
                    <Button asChild className="rounded-full bg-[#2A1B1A] hover:bg-[#3a2725] text-white font-semibold h-11 px-6 btn-press">
                      {w.href.startsWith('/') && !w.href.includes('#') ? (
                        <Link to={w.href}>
                          {w.cta} <ArrowRight className="h-4 w-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      ) : (
                        <a href={w.href}>
                          {w.cta} <ArrowRight className="h-4 w-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
                        </a>
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Volunteer call out */}
      <section id="volunteer" className="py-16">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="relative rounded-[28px] overflow-hidden bg-[#5DCAB5]">
            <div className="grid lg:grid-cols-2 items-center">
              <div className="p-9 lg:p-14 text-white">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 text-white text-[12px] font-semibold uppercase tracking-[0.18em]">
                  <Sparkles className="h-3.5 w-3.5" /> {t('gi.vol.eyebrow')}
                </div>
                <h2 className="font-serif-display text-[36px] lg:text-[48px] leading-[1.05] font-bold mt-5">
                  {t('gi.vol.title1')} <span className="italic">{t('gi.vol.title2')}</span> {t('gi.vol.title3')}
                </h2>
                <p className="mt-5 text-white/90 text-[16px] leading-relaxed">{t('gi.vol.desc')}</p>
                <Button asChild className="mt-7 rounded-full bg-white text-[#5DCAB5] hover:bg-[#FAF3EE] font-semibold h-12 px-7 btn-press">
                  <a href="mailto:info@gemsatthearts.org?subject=Volunteer Interest">{t('gi.vol.cta')}</a>
                </Button>
              </div>
              <div className="h-[260px] lg:h-[440px]">
                <img src={PHOTO_LEADERSHIP} alt="Volunteers and community members gathered at GEMS @ The Arts" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partner callout */}
      <section id="partner" className="py-16 pb-24">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="relative rounded-[28px] overflow-hidden bg-[#2A1B1A]">
            <div className="grid lg:grid-cols-2 items-center">
              <div className="h-[260px] lg:h-[440px] order-2 lg:order-1">
                <img src={PHOTO_MURAL} alt="Framingham community mural" className="w-full h-full object-cover" />
              </div>
              <div className="p-9 lg:p-14 text-white order-1 lg:order-2">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FCD968] text-[#2A1B1A] text-[12px] font-semibold uppercase tracking-[0.18em]">
                  <Handshake className="h-3.5 w-3.5" /> {t('gi.partner.eyebrow')}
                </div>
                <h2 className="font-serif-display text-[36px] lg:text-[48px] leading-[1.05] font-bold mt-5">
                  {t('gi.partner.title1')} <span className="italic text-[#FCD968]">{t('gi.partner.title2')}</span>
                </h2>
                <p className="mt-5 text-white/85 text-[16px] leading-relaxed">{t('gi.partner.desc')}</p>
                <div className="flex flex-wrap gap-3 mt-7">
                  <Button asChild className="rounded-full bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold h-12 px-7 btn-press">
                    <a href="mailto:info@gemsatthearts.org?subject=Partnership Inquiry">{t('gi.partner.cta1')}</a>
                  </Button>
                  <Button asChild variant="outline" className="rounded-full border-2 border-white/40 text-white hover:bg-white hover:text-[#2A1B1A] bg-transparent font-semibold h-12 px-7 btn-press">
                    <Link to="/donate">{t('gi.partner.cta2')}</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
